import React, { forwardRef } from "react";
import { IQuickFilter, QuickFilterTotals } from "./FilteredTable.types";
import { useStaffMode } from "../../../hooks/StaffModeProvider";

interface QuickFiltersBarProps {
  quickFilters: IQuickFilter[];
  selectedQuickFilter: string | null;
  quickFilterTotalCount: QuickFilterTotals;
  onQuickFilterClick: (key: string) => void;
  quickFilterBarAccessory?: React.ReactNode;
  lockActiveQuickFilter?: boolean;
  /** When false, tab labels omit the (count) suffix. Default true. */
  showCounts?: boolean;
  /** Sticky chip row while scrolling the filter panel and data (opt-in from FilteredTableCore). */
  sticky?: boolean;
  /** When false, top corners are square (use an outer wrapper for rounding). Default true. */
  roundedTop?: boolean;
  /** Pill tabs for firm logs modal (blue active/inactive styling, no bar background). */
  variant?: "default" | "firmLogs" | "panel";
}

const QuickFiltersBar = forwardRef<HTMLDivElement, QuickFiltersBarProps>(
  function QuickFiltersBar(
    {
      quickFilters,
      selectedQuickFilter,
      quickFilterTotalCount,
      onQuickFilterClick,
      quickFilterBarAccessory,
      lockActiveQuickFilter = false,
      showCounts = true,
      sticky = false,
      roundedTop = true,
      variant = "default",
    },
    ref,
  ) {
    const { isStaffMode } = useStaffMode();
    const isFirmLogs = variant === "firmLogs";
    const isPanel = variant === "panel";
    return (
      <div
        ref={ref}
        className={`flex w-full items-center gap-4 overflow-x-auto ${
          isFirmLogs || isPanel
            ? "bg-white py-0"
            : `border-b ${roundedTop ? "rounded-t-lg" : ""} ${isStaffMode ? "bg-accent-Default-dark" : "bg-gray-15"} px-6 py-3`
        } ${sticky ? "sticky top-0 z-40 shrink-0 shadow-md" : ""}`}
      >
        <div className={`flex shrink-0 flex-wrap ${isFirmLogs ? "gap-2" : "gap-4"}`}>
          {quickFilters.map((quickFilter) => {
            const isActive = selectedQuickFilter === quickFilter.key;
            const isLockedActive = lockActiveQuickFilter && isActive;
            const firmLogsTabClass = isActive
              ? "cursor-pointer bg-accent-Default-light text-Default font-semibold"
              : "cursor-pointer bg-accent-Default-dark text-white font-semibold hover:bg-[#003a66]";
            return (
              <button
                key={quickFilter.key}
                type="button"
                onClick={() => onQuickFilterClick(quickFilter.key)}
                className={`shrink-0 rounded px-4 py-2 text-sm transition-colors lg:tex-base ${
                  isFirmLogs
                    ? firmLogsTabClass
                    : `min-w-48 font-medium ${
                        isLockedActive
                          ? `cursor-default ${isStaffMode ? "bg-accent-Default-light text-gray-font" : "bg-primary-dark text-white"}`
                          : isActive
                            ? "cursor-pointer bg-accent-Default-light text-accent-Default-dark"
                            : `cursor-pointer ${isStaffMode ? "bg-secondary hover:bg-accent-Default-dark" : "bg-gray-font hover:bg-gray-600"} text-white `
                      }`
                }`}
              >
                {quickFilter.label}
                {showCounts
                  ? ` (${quickFilterTotalCount[quickFilter.key] ?? 0})`
                  : ""}
              </button>
            );
          })}
        </div>
        {quickFilterBarAccessory && (
          <div className="ml-auto shrink-0 pl-2">{quickFilterBarAccessory}</div>
        )}
      </div>
    );
  },
);

export default QuickFiltersBar;
